/**
 * Auth-specific error mapping on top of the shared provider error mapper.
 * Raw Supabase messages never reach the user; they land in technicalMessage.
 */
import type { AppError } from '@/types'
import { makeAppError, newCorrelationId, toAppError } from '@/lib/supabase/errors'

function authError(code: string, userMessage: string, technicalMessage: string, retryable = false): AppError {
  return makeAppError({
    code,
    userMessage,
    technicalMessage: technicalMessage.slice(0, 500),
    retryable,
    source: 'auth',
    correlationId: newCorrelationId(),
  })
}

/**
 * Maps Supabase Auth errors to safe, specific Dutch AppError values.
 * Falls back to the shared toAppError() mapper for anything unrecognized
 * (network, rate limit, unknown).
 */
export function toAuthError(error: unknown): AppError {
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message?: unknown }).message ?? '')
      : ''
  const msg = message.toLowerCase()

  if (msg.includes('invalid login credentials')) {
    return authError('AUTH_INVALID_CREDENTIALS', 'E-mailadres of wachtwoord is niet correct.', message)
  }
  if (msg.includes('email not confirmed')) {
    return authError(
      'AUTH_EMAIL_NOT_CONFIRMED',
      'Je e-mailadres is nog niet bevestigd. Open de link in de e-mail die je hebt ontvangen.',
      message,
    )
  }
  if (msg.includes('user already registered') || msg.includes('already been registered')) {
    return authError('AUTH_EMAIL_IN_USE', 'Er bestaat al een account met dit e-mailadres. Probeer in te loggen.', message)
  }
  if (msg.includes('password') && (msg.includes('at least') || msg.includes('weak'))) {
    return authError('AUTH_PASSWORD_TOO_WEAK', 'Dit wachtwoord is te zwak. Kies een sterker wachtwoord.', message)
  }
  if (msg.includes('signup') && msg.includes('disabled')) {
    return authError('AUTH_SIGNUP_DISABLED', 'Registreren is tijdelijk niet beschikbaar. Probeer het later opnieuw.', message, true)
  }
  if (msg.includes('refresh token') || msg.includes('session') && msg.includes('missing')) {
    return authError('AUTH_SESSION_INVALID', 'Je sessie is verlopen. Log opnieuw in.', message)
  }
  return toAppError(error, 'auth')
}

/** Role mismatch after a technically successful sign-in. */
export function roleMismatchError(expected: 'client' | 'caregiver'): AppError {
  const userMessage =
    expected === 'client'
      ? 'Dit account hoort bij een begeleider. Log in via de begeleiderslogin.'
      : 'Dit account hoort bij een cliënt. Log in via de cliëntenlogin.'
  return authError('AUTH_ROLE_MISMATCH', userMessage, `expected role=${expected}`)
}

/** Profile row could not be read after a successful auth call. */
export function profileUnavailableError(): AppError {
  return authError(
    'AUTH_PROFILE_UNAVAILABLE',
    'Je profiel kon niet worden geladen. Probeer het later opnieuw.',
    'profile bootstrap returned null',
    true,
  )
}

/** Supabase env config missing — honest, non-retryable state. */
export function notConfiguredError(): AppError {
  return makeAppError({
    code: 'SUPABASE_NOT_CONFIGURED',
    userMessage: 'De cloudverbinding is niet ingesteld. Inloggen en registreren zijn nu niet beschikbaar.',
    technicalMessage: 'VITE_SUPABASE_URL or a Supabase publishable/anon key is missing',
    retryable: false,
    source: 'auth',
  })
}
